import { groupBy, sortBy, sumBy, uniq } from "lodash-es";

import { compareOldestFirst } from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import { largestRemainder } from "@/forwards2/picker/distributeTakes";

/** The slice of a portfolio row the auto-select allocation reads. */
export interface AllocatableItem {
  id: number;
  count: number;
  company_id: number;
  production_month: string | null;
}

const MAX_SUBSET_SEARCH_WORK = 2_000_000;

export interface Allocation {
  id: number;
  count: number;
}

/**
 * Picks `mwhNeeded` MWh (rounded down to whole MWh) from `items`, oldest production month first and smaller rows first within a
 * month, so the oldest certificates leave inventory before they expire.
 *
 * With `spreadOverCompanies` the target is shared out across companies in proportion to what each
 * holds (see {@link allocateSpreadOverCompanies}); otherwise rows are taken greedily in that order.
 * Callers filter `items` down to what may be sold first.
 */
export function allocateAutoSelection(
  items: AllocatableItem[],
  mwhNeeded: number,
  { spreadOverCompanies }: { spreadOverCompanies: boolean }
): Allocation[] {
  // Certificates are whole MWh; a fractional or non-finite target would reach the API as-is.
  const target = Number.isFinite(mwhNeeded) ? Math.floor(mwhNeeded) : 0;
  if (target <= 0) return [];
  const ordered = [...items].sort(compareOldestFirst);
  return spreadOverCompanies
    ? allocateSpreadOverCompanies(ordered, target)
    : allocateGreedy(ordered, target);
}

const allocateGreedy = (
  items: AllocatableItem[],
  mwhNeeded: number
): Allocation[] => {
  const allocations: Allocation[] = [];
  let remaining = mwhNeeded;
  for (const item of items) {
    if (remaining <= 0) break;
    const count = Math.min(item.count, remaining);
    allocations.push({ id: item.id, count });
    remaining -= count;
  }
  return allocations;
};

// Two phases:
//   1. Share the target out across companies in proportion to what each holds, by the
//      largest-remainder (Hamilton) method the forward picker uses too ({@link largestRemainder}):
//      ties go to the bigger company, then the lower company_id. A target past the total takes
//      every company's full holding.
//   2. For each company, fulfil its share from its rows: an exact subset when one exists, else
//      walking them in the shared sort order (production_month asc, count asc) and taking whole rows
//      where possible — a partial take only happens for the last row that crosses the company's
//      share (at most one split per company).
const allocateSpreadOverCompanies = (
  items: AllocatableItem[],
  mwhNeeded: number
): Allocation[] => {
  const byCompany = groupBy(items, (item) => item.company_id);
  const companyIds = sortBy(Object.keys(byCompany).map(Number));
  const shares = largestRemainder(
    mwhNeeded,
    companyIds.map((cid) => sumBy(byCompany[cid], "count"))
  );
  const shareOf = new Map(companyIds.map((cid, index) => [cid, shares[index]]));

  // Phase 2 walks the companies in the rows' own order, oldest rows' company first.
  return uniq(items.map((item) => item.company_id)).flatMap((cid) => {
    const companyItems = byCompany[cid];
    const target = shareOf.get(cid) ?? 0;
    const exactSubset = findExactSubsetSum(companyItems, target);
    return exactSubset
      ? exactSubset.map((item) => ({ id: item.id, count: item.count }))
      : allocateGreedy(companyItems, target);
  });
};

// Subset-sum DP. Returns a subset of `items` whose counts sum exactly to
// `target`, or null if none exists.
const findExactSubsetSum = (
  items: AllocatableItem[],
  target: number
): AllocatableItem[] | null => {
  if (target === 0) return [];
  if (target < 0 || items.length === 0) return null;
  if (!items.every((i) => Number.isInteger(i.count))) return null;
  // PERF: the search is O(items × target) on the UI thread; past this bound the greedy fallback runs.
  if (items.length * target > MAX_SUBSET_SEARCH_WORK) return null;

  const reachedBy = new Map<number, number>();
  const prevSum = new Map<number, number>();
  reachedBy.set(0, -1);

  for (let i = 0; i < items.length; i++) {
    const count = items[i].count;
    if (count <= 0) continue;
    const snapshot = Array.from(reachedBy.keys());
    for (const s of snapshot) {
      const newSum = s + count;
      if (newSum > target || reachedBy.has(newSum)) continue;
      reachedBy.set(newSum, i);
      prevSum.set(newSum, s);
      if (newSum === target) {
        const result: AllocatableItem[] = [];
        let cur = target;
        while (cur > 0) {
          const idx = reachedBy.get(cur)!;
          result.push(items[idx]);
          cur = prevSum.get(cur)!;
        }
        return result;
      }
    }
  }
  return null;
};
